import { httpClient } from '../core';
import { UserIdentityModel } from '../models/file-entities.model';
import Vue from 'vue';
import i18n from '../i18n';
import { env } from '../env';
import { changeLocale, normalizeLocale } from '../utils/Locale';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

const translate = (message: string) => i18n.t(message).toString();

const normalizeUserIdentity = (userIdentityParams: any): UserIdentityModel => {
  const idioma = normalizeLocale(userIdentityParams?.idioma ?? userIdentityParams?.Idioma ?? 'es');

  return {
    ...userIdentityParams,
    idioma,
    Idioma: idioma,
  } as UserIdentityModel;
};

export interface UserState {
  userIdentity: UserIdentityModel;
  isFetchingUserIdentity: boolean;
}

export const state: UserState = {
  userIdentity: {} as UserIdentityModel,
  isFetchingUserIdentity: false,
};

export const getters: GetterTree<UserState, any> = {
  getUserIdentity: () => state.userIdentity,
  isFetchingUserIdentity: () => state.isFetchingUserIdentity,
};

export const mutations: MutationTree<UserState> = {
  SET_USER_IDENTITY(userState, userIdentityParams: any) {
    userState.userIdentity = normalizeUserIdentity(userIdentityParams);
    userState.isFetchingUserIdentity = false;
  },
  SET_FETCHING_USER_IDENTITY() {
    state.isFetchingUserIdentity = true;
  },
  UNSET_FETCHING_USER_IDENTITY() {
    state.isFetchingUserIdentity = false;
  },
  setUserIdentity(userState, userIdentityParams: UserIdentityModel) {
    userState.userIdentity = normalizeUserIdentity(userIdentityParams);
  },
  setToken(userState, token: string) {
    userState.userIdentity.token = token;
  },
  setIdioma(userState, idioma: string) {
    const normalizedIdioma = normalizeLocale(idioma);
    userState.userIdentity = {
      ...userState.userIdentity,
      idioma: normalizedIdioma,
      Idioma: normalizedIdioma,
    };
  },
};

const loginWithPingOne = async ({ commit }, payload: any) => {
  try {
    const response = await httpClient(env.apiUrl || '').instance.post('/api/login/', payload);

    if (response && response.status === 200) {
      const userIdentity = normalizeUserIdentity(response.data);
      await commit('setUserIdentity', userIdentity);
      changeLocale(userIdentity.idioma || 'es', true);
      return userIdentity;
    }

    (Vue as any).$toast.error(translate('response_error.login'));
    return false;
  } catch (error: any) {
    const status = error?.response?.status;

    if (status === 400) {
      (Vue as any).$toast.error(translate('response_error.login_missing_data'));
      return false;
    }

    if (status === 401) {
      (Vue as any).$toast.error(translate('response_error.login_unauthorized'));
      return false;
    }

    (Vue as any).$toast.error(translate('response_error.login'));
    return false;
  }
};

export const actions: ActionTree<UserState, any> = {
  fetchLoginInternal: async ({ commit }, payload: any) => {
    const response = await httpClient(env.apiAuthUrl || env.apiUrl || '').instance.post('/api/login/', payload);

    if (response && response.status === 200) {
      const userIdentity = normalizeUserIdentity(response.data);
      await commit('setUserIdentity', userIdentity);
      changeLocale(userIdentity.idioma || 'es', true);
      return userIdentity;
    }

    (Vue as any).$toast.error(translate('response_error.login'));
    return false;
  },

  fetchLoginProvicional: async ({ commit }, payload: any) => {
    try {
      const response = await httpClient(env.apiUrl || '').instance.post('/api/login/email-borrar', payload);

      if (response && response.status === 200) {
        const userIdentity = normalizeUserIdentity(response.data);
        await commit('setUserIdentity', userIdentity);
        changeLocale(userIdentity.idioma || 'es', true);
        return userIdentity;
      }

      (Vue as any).$toast.error(translate('response_error.login'));
      return false;
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 400) {
        (Vue as any).$toast.error(translate('response_error.login_missing_data'));
        return false;
      }

      if (status === 401) {
        (Vue as any).$toast.error(translate('response_error.login_unauthorized'));
        return false;
      }

      (Vue as any).$toast.error(translate('response_error.login'));
      return false;
    }
  },

  fetchLoginPingOne: async ({ commit }, payload: any) => {
    return loginWithPingOne({ commit }, payload);
  },

  updateIdioma: async ({ commit }, payload: { id: number; idioma: string }) => {
    try {
      const response = await httpClient(env.apiUrl || '').instance.put(`/api/usuarios/${payload.id}/idioma`, {
        idioma: payload.idioma,
      });

      const savedIdioma = response?.data?.idioma ?? response?.data?.Idioma ?? payload.idioma;
      const normalizedIdioma = normalizeLocale(savedIdioma);

      commit('setIdioma', normalizedIdioma);
      changeLocale(normalizedIdioma, true);

      return normalizedIdioma;
    } catch (error: any) {
      const status = error?.response?.status;
      const backendMessage = error?.response?.data?.message;

      if (status === 400 || status === 404) {
        (Vue as any).$toast.error(backendMessage || i18n.t('response_error.update_entity_status', { status }).toString());
        return false;
      }

      (Vue as any).$toast.error(i18n.t('response_error.update_entity_status', { status: status ?? 'unknown' }).toString());
      return false;
    }
  },
};

export const userIdentity: Module<UserState, any> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
